import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { PostsAdmin } from "./PostsAdmin";

const inp = "mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-navy outline-none focus:border-brand";
const errText = (e, t) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? t("admin.invalid") : t("common.error");
};

const Field = ({ label, value, onChange, testId, ...rest }) => (
  <label className="block text-xs text-slate-500">{label}<input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={inp} {...rest} /></label>
);

const useLoad = (url, t) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(url).then((r) => { if (live) setData((d) => d || r.data); }).catch(() => toast.error(t("common.error")));
    return () => { live = false; };
  }, [url, t]);
  return [data, setData];
};

const Section = ({ title, sub, children, onSave, testId }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); try { await onSave(); toast.success(t("admin.saved")); } catch (e) { toast.error(errText(e, t)); } finally { setBusy(false); } };
  return (
    <section className="space-y-3">
      <div><h2 className="text-base font-semibold text-navy md:text-lg">{title}</h2>{sub && <p className="text-xs text-slate-500">{sub}</p>}</div>
      <div className="grid gap-3 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-2">{children}</div>
      <button type="button" disabled={busy} onClick={save} data-testid={testId} className="h-11 w-full rounded-full bg-brand text-sm font-semibold text-white disabled:opacity-60 sm:w-48">{t("admin.save")}</button>
    </section>
  );
};

export default function ContentAdmin() {
  const { t } = useI18n();
  const [content, setContent] = useLoad("/content/admin", t);
  const [pre, setPre] = useLoad("/preseason/settings", t);
  const chat = (k) => (v) => setContent((d) => ({ ...d, chat: { ...d.chat, [k]: v } }));
  const ps = (k) => (v) => setPre((d) => ({ ...d, [k]: v }));

  return (
    <div className="space-y-8" data-testid="content-admin">
      <div><h1 className="text-3xl font-bold text-navy">{t("admin.contentTitle")}</h1></div>
      <PostsAdmin />
      {content && (
        <Section title={t("admin.chatLinks")} testId="content-save-button" onSave={async () => setContent((await api.put("/content/admin", content)).data)}>
          <Field label={t("admin.whatsappNumber")} value={content.chat.whatsapp_number} onChange={chat("whatsapp_number")} testId="chat-whatsapp-input" />
          <Field label={t("admin.respondioUrl")} value={content.chat.respondio_url} onChange={chat("respondio_url")} testId="chat-respondio-input" />
        </Section>
      )}
      {pre && (
        <Section title={t("preseason.settings")} sub={t("preseason.settingsSub")} testId="preseason-save-button"
          onSave={async () => setPre((await api.put("/preseason/settings", { ...pre, weeks: Number(pre.weeks) })).data)}>
          <Field label={t("preseason.windowStart")} value={pre.window_start} onChange={ps("window_start")} testId="preseason-window-start" placeholder="03-01" maxLength={5} />
          <Field label={t("preseason.windowEnd")} value={pre.window_end} onChange={ps("window_end")} testId="preseason-window-end" placeholder="04-30" maxLength={5} />
          <Field label={t("preseason.seasonStart")} value={pre.season_start} onChange={ps("season_start")} testId="preseason-season-start" placeholder="04-01" maxLength={5} />
          <Field label={t("preseason.weeks")} value={pre.weeks} onChange={ps("weeks")} testId="preseason-weeks" type="number" min={1} max={12} />
        </Section>
      )}
    </div>
  );
}
